import type { InputType, MfaChallengeProps } from "./mfaTypes";
import { useEffect, useMemo, useState } from "react";
import { useChallenge } from "./mfaHooks";
import { Flex, Text } from "@chakra-ui/react";
import { InputGroup } from "components/input/InputGroup";
import { ModalButtons } from "components/modal/Modal";
import { Button } from "components/button/Button";
import { PinInput } from "components/ui/pin-input";

export const MfaChallenge = ({
  shouldVerifyOnClick = false,
  canUseRecoveryCode = false,
  verify,
  authVars,
  onVerify,
  onConfirm = () => {},
  onCancel = () => {},
  confirmText = "Next",
  appText,
  recoveryText,
  appSubtext,
  recoverySubText,
  isDangerous,
  size = "sm",
  children, //render prop used to make custom buttons
}: MfaChallengeProps) => {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(""));
  const [recoveryCode, setRecoveryCode] = useState("");
  const [inputType, setInputType] = useState<InputType>("digits");
  const [isConfirmed, setIsConfirmed] = useState(false);
  const authCode = inputType === "digits" ? digits.join("") : recoveryCode;
  const { hasError, setHasError, isVerified, isSubmitting, isComplete } =
    useChallenge({
      verify,
      authVars,
      authCode,
      shouldVerify: !shouldVerifyOnClick || isConfirmed,
      inputType,
    });

  const handleDigitChange = (value: string[]) => {
    setDigits(value);
    setHasError(false);
    setIsConfirmed(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAuthCode = e.target.value.substring(0, 11).toUpperCase();
    if (authCode !== newAuthCode) {
      setRecoveryCode(newAuthCode);
      setHasError(false);
      setIsConfirmed(false);
    }
  };

  const handleConfirm = (isVerified: boolean) => {
    setIsConfirmed(true);
    onConfirm(isVerified);
  };

  useEffect(() => {
    if (isVerified && onVerify) {
      onVerify();
    }
  }, [isVerified, onVerify]);

  const [text, subText] = useMemo(
    () => [
      (inputType === "digits" && appText) ||
        (inputType === "recoveryCode" && recoveryText),
      (inputType === "digits" && appSubtext) ||
        (inputType === "recoveryCode" && recoverySubText),
    ],
    [inputType, appText, appSubtext, recoverySubText, recoveryText],
  );

  return (
    <Flex direction="column" gap="8" alignItems={"stretch"} width="100%">
      {text && (
        <Text as="span" textAlign={"center"}>
          {text}
        </Text>
      )}
      <Flex direction="column" gap="2" alignItems="stretch">
        {subText && (
          <Text as="span" textAlign="center">
            {subText}
          </Text>
        )}
        {inputType === "digits" ? (
          <Flex direction="column" gap="4" alignItems="center">
            <PinInput
              value={digits}
              onValueChange={(details) => handleDigitChange(details.value)}
              invalid={hasError}
              size={size}
              count={6}
              type="numeric"
              placeholder=""
              otp
              autoFocus
              data-cy="mfa-otp-code"
            />
            {hasError && (
              <Text
                as="span"
                color="error.fg"
                textStyle="caption"
                data-cy="mfa-otp-error"
              >
                The code you entered is incorrect
              </Text>
            )}
          </Flex>
        ) : (
          <InputGroup
            width="100%"
            value={
              authCode.length < 6
                ? authCode.toUpperCase()
                : `${authCode.substring(0, 5)}-${authCode.substring(5, 15)}`
                    .split(/-+/)
                    .join("-")
                    .substring(0, 11)
                    .toUpperCase()
            }
            autoFocus
            textAlign="center"
            onChange={handleInputChange}
            error={
              hasError
                ? "The code you entered is incorrect or has already been used."
                : ""
            }
          />
        )}
      </Flex>
      {children ? (
        children({
          confirm: () => handleConfirm(isVerified),
          isSubmitting,
          hasError,
          isComplete,
        })
      ) : (
        <ModalButtons
          mt="0"
          isDangerous={isDangerous}
          primaryCTA={{
            onClick: () => handleConfirm(isVerified),
            text: confirmText,
            isLoading: isSubmitting,
            isDisabled: hasError || isSubmitting || !isComplete,
          }}
          secondaryCTA={{
            onClick: onCancel,
            text: "Cancel",
          }}
        />
      )}
      {canUseRecoveryCode && (
        <Button
          variant={"link"}
          textStyle={"captionLink"}
          maxW={"unset"}
          alignSelf="center"
          onClick={() => {
            setDigits(Array(6).fill(""));
            setRecoveryCode("");
            setInputType(inputType === "digits" ? "recoveryCode" : "digits");
          }}
        >
          {inputType === "digits"
            ? "Use recovery code instead"
            : "Use two-factor authentication code instead"}
        </Button>
      )}
    </Flex>
  );
};
